import 'twin.macro';
import { ElevesView } from '@teetsh/app/src/types/eleves';
import { PlusIcon as Plus } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import { elevesUrls } from '@teetsh/app/src/containers/Eleves/urls';
import Popover from '@teetsh/app/src/components/Popover';
import { NewViewContent } from '@teetsh/app/src/containers/Eleves/List/Table/Views/NewViewContent';
import { useSchoolGoTo } from '@teetsh/app/src/containers/App/useSchoolGoTo';
import { RenameViewModal } from '@teetsh/app/src/containers/Eleves/List/Table/Views/RenameView';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { SortableItem } from './SortableItem';
import { ActiveItem } from 'containers/Eleves/List/Table/Views/ActiveItem';
import { InactiveItem } from 'containers/Eleves/List/Table/Views/InactiveItem';
import React, { useState } from 'react';
import findIndex from 'lodash-es/findIndex';
import changeItemIndex from 'utils/arrays/changeItemAtIndex';
import useReorderElevesView from 'queries/eleves/views/useReorderElevesView';
import { useTranslation } from 'react-i18next';

interface Props {
  views: ElevesView[];
}

export function Views(props: Props) {
  const { views } = props;
  const { t } = useTranslation(['common']);
  const { pathname } = useLocation();
  const goTo = useSchoolGoTo();
  const reorder = useReorderElevesView();

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const [activeId, setActiveId] = useState(null);

  async function handleDragEnd(event) {
    const { active, over } = event;
    if (!over) {
      return null;
    }
    const fromIndex = findIndex(views, d => d.id === active.id);
    const toIndex = findIndex(views, d => d.id === over.id);
    await reorder(
      changeItemIndex(views || [], fromIndex, toIndex).map(d => d.id),
    );
    setActiveId(null);
    return null;
  }

  function handleDragStart(event) {
    const { active } = event;

    setActiveId(active.id);
  }

  return (
    <div tw="grow min-w-0" key={pathname}>
      <RenameViewModal />
      <div tw="sm:hidden my-2">
        <select
          id="tabs"
          name="tabs"
          tw="block w-full pl-3 pr-10 py-2 text-base border-neutral-quiet focus:outline-none focus:ring-brand-intense focus:border-brand-intense sm:text-sm rounded-md"
          onChange={e => goTo(elevesUrls.viewDetails(e.target.value))}
          value={views.find(v => pathname.includes(v.id))?.id}
        >
          {views.map(v => (
            <option className="group" key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>
      <div tw="hidden sm:block">
        <div tw="flex items-center">
          <nav
            tw="-mb-px flex space-x-8 overflow-x-auto flex-1 min-w-0"
            aria-label="Tabs"
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={views.map(d => d.id)}
                strategy={horizontalListSortingStrategy}
              >
                {views.map(v => {
                  const active = pathname.includes(v.id);
                  if (active) {
                    return (
                      <SortableItem key={v.id} id={v.id}>
                        <ActiveItem view={v} />
                      </SortableItem>
                    );
                  }

                  return (
                    <SortableItem key={v.id} id={v.id}>
                      <InactiveItem view={v} />
                    </SortableItem>
                  );
                })}
                <DragOverlay>
                  {activeId ? (
                    <div
                      tw="transform rotate-2 text-muted whitespace-nowrap text-sm pt-4 font-medium"
                      id={activeId}
                    >
                      {views.find(v => v.id === activeId)?.name}
                    </div>
                  ) : null}
                </DragOverlay>
              </SortableContext>
            </DndContext>
          </nav>
          <Popover as="div" flex portal>
            <div tw="flex items-center h-full ml-8">
              <Popover.Button
                minimal
                icon={Plus}
                tw="text-gray-500!"
                aria-label="Ajouter une vue"
              >
                {t('common:buttons.addView')}
              </Popover.Button>
            </div>

            <Popover.Panel>
              <div tw="p-4">
                <NewViewContent />
              </div>
            </Popover.Panel>
          </Popover>
        </div>
      </div>
    </div>
  );
}
